import React, { useEffect, useState } from 'react';

import moment from 'moment';

import { fetchTravelExpenses, fetchTravelExpensesRappi, importRappiExpense } from 'actions/expense';
import { getRappiExpensesByCountry } from 'actions/expense';
import { Expense as ExpenseItem } from 'components/ExpensesSelectableList';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { TravelExpensesResponse, Expense, RappiExpensesFilters, Order } from 'models/expense';
import { extractErrorMessage } from 'utils/generic';

import ExpensesListImport from '../components/ExpensesListImport';

interface Params {
  match: {
    params: {
      travelId: string;
    };
  };
}

const ExpensesListImportContainer = ({ match: { params } }: Params) => {
  const { travelId } = params;
  const toast = useToast();
  const { t } = useStoreTranslation();
  const [isLoadingTravel, setIsLoadingTravel] = useState(true);
  const [isLoadingRappi, setIsLoadingRappi] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [travel, setTravel] = useState<TravelExpensesResponse>();
  const userAccount = useUserAccount();
  const [expenses, setExpenses] = useState<ExpenseItem[]>();
  const [currentExpenses, setCurrentExpenses] = useState<ExpenseItem[]>([]);
  const [rappiOrders, setRappiOrders] = useState<Order[]>([]);
  const [dateRange, setDateRange] = useState<{ initialDate: Date; finalDate: Date }>({
    initialDate: new Date(),
    finalDate: new Date(),
  });
  const [totalExpenses, setTotalExpenses] = useState<number>(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSelect = (expense: ExpenseItem) => {
    const funded = currentExpenses.find((item) => item.id === expense.id);
    if (funded) {
      setCurrentExpenses(currentExpenses.filter((item) => item.id !== expense.id));

      const newTotalExpenses = totalExpenses - funded.value;

      setTotalExpenses(newTotalExpenses);
    } else {
      setCurrentExpenses([...currentExpenses, expense]);

      const newTotalExpenses = totalExpenses + expense.value;

      setTotalExpenses(newTotalExpenses);
    }
  };

  const handleFetchExpenses = async () => {
    const {
      data: { data },
    } = await fetchTravelExpenses(params.travelId);
    setTravel(data);
    setDateRange({
      initialDate: moment(data.checkin).add(-1, 'days').toDate(),
      finalDate: moment(data.checkout).add(1, 'days').toDate(),
    });
    setIsLoadingTravel(false);
  };

  const handleFetchRappi = async () => {
    const filters: RappiExpensesFilters = {
      StartDate: dateRange.initialDate,
      EndDate: dateRange.finalDate,
    };

    try {
      setIsLoadingRappi(true);
      const {
        data: {
          data: { orders },
        },
      } = await getRappiExpensesByCountry(userAccount?.idRappi || '', filters);

      const {
        data: { data },
      } = await fetchTravelExpensesRappi(params.travelId);

      const filteredOrders = orders.filter(
        (rappiOrder) => !data.some((expense) => expense.rappiOrderId === rappiOrder.orderId.toString()),
      );

      setRappiOrders(filteredOrders);
      setExpenses(
        filteredOrders.map(
          (expense) =>
            ({
              currency: userAccount?.countryCurrency,
              date: expense.createdAt,
              fromRappi: true,
              id: expense.orderId,
              name: expense.stores[0].name,
              value: expense.totalValue,
              type: 'Others',
            } as ExpenseItem),
        ),
      );
    } catch (err) {
      toast.add({ type: 'danger', message: extractErrorMessage(err) || t('defaultError.default') });
    }

    setCurrentExpenses([]);
    setIsLoadingRappi(false);
  };

  useEffect(() => {
    if (travelId) {
      handleFetchExpenses();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // TODO: check dependencies or refactor

  useEffect(() => {
    handleFetchRappi();
  }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps
  // TODO: check dependencies or refactor

  const handleImport = async () => {
    const listImport = rappiOrders
      .filter((order) => currentExpenses.some((e) => e.id === order.orderId))
      .map(
        (e) =>
          ({
            travelId: params.travelId,
            currencyCode: userAccount?.countryCurrency,
            value: e.totalValue,
            date: e.createdAt,
            type: 'Others',
            comments: e.comments,
            name: e.stores[0] ? e.stores[0]?.name : 0,
            rappiOrderId: e.orderId,
            rappiOrder: {
              id: e.orderId,
              addressDescription: e.addressDescription,
              comment: e.comments,
              createdAt: e.createdAt,
              discount: e.totalOrderDiscount,
              name: e.stores[0] ? e.stores[0]?.name : 0,
              paymentMethod: e.paymentMethod,
              state: e.state,
              totalValue: e.totalValue,
            },
          } as Expense),
      );

    try {
      setIsImporting(true);
      await importRappiExpense(params.travelId, listImport);
      setShowSuccess(true);
    } catch (ex) {
      toast.add({ type: 'danger', message: extractErrorMessage(ex) || t('defaultError.default') });
    } finally {
      setIsImporting(false);
    }
  };

  const handleChangeDate = (dates: { initialDate?: Date; finalDate?: Date }) => {
    setDateRange({ initialDate: dates.initialDate || new Date(), finalDate: dates.finalDate || new Date() });
  };

  return (
    <ExpensesListImport
      travel={travel}
      expenses={expenses}
      currentExpenses={currentExpenses.map((item) => item.id)}
      isLoadingRappi={isLoadingRappi}
      isLoadingTravel={isLoadingTravel}
      isImporting={isImporting}
      initialDate={dateRange.initialDate || new Date()}
      finalDate={dateRange.finalDate || new Date()}
      onSelect={handleSelect}
      onChangeDate={handleChangeDate}
      onImport={handleImport}
      totalExpenses={totalExpenses}
      travelId={params.travelId}
      currency={userAccount?.countryCurrency || ''}
      showSuccess={showSuccess}
    />
  );
};

export default ExpensesListImportContainer;
